import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface NotificationsContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAgent } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      if (isAgent) {
        // For agents: sum all unread_count_agent across all conversations
        const { data, error } = await supabase
          .from('conversations')
          .select('unread_count_agent')
          .neq('status', 'closed');

        if (!error && data) {
          const total = data.reduce((sum, conv) => sum + (conv.unread_count_agent || 0), 0);
          setUnreadCount(total);
        }
      } else {
        // For users: sum all unread_count_user across their conversations
        const { data, error } = await supabase
          .from('conversations')
          .select('unread_count_user')
          .eq('user_id', user.id);

        if (!error && data) {
          const total = data.reduce((sum, conv) => sum + (conv.unread_count_user || 0), 0);
          setUnreadCount(total);
        }
      }
    } catch (error) {
      console.error('[NotificationsContext] Error fetching unread count:', error);
    }
  }, [user, isAgent]);

  // Initial fetch
  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  // Update PWA app badge when unread count changes
  useEffect(() => {
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        (navigator as Navigator & { setAppBadge: (count: number) => Promise<void> }).setAppBadge(unreadCount).catch(console.error);
      } else {
        (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.().catch(console.error);
      }
    }
  }, [unreadCount]);

  // Subscribe to realtime updates on conversations
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('unread-count-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          // Refetch when any conversation is updated
          refreshUnreadCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          refreshUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refreshUnreadCount]);

  return (
    <NotificationsContext.Provider value={{ unreadCount, refreshUnreadCount }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider');
  }
  return context;
}
